import React from 'react';
import moment from 'moment';
import {v4 as uuidv4} from 'uuid';

import BgBlurboxPattern from '../assets/svgComponents/BgBlurboxPattern';
import BgBubblesPattern from '../assets/svgComponents/BgBubblesPattern';
import BgHexaflowPattern from '../assets/svgComponents/BgHexaflowPattern';
import BgOrbitalPattern from '../assets/svgComponents/BgOrbitalPattern';
import BgWaveformPattern from '../assets/svgComponents/BgWaveformPattern';
import BgWireframePattern from '../assets/svgComponents/BgWireframePattern';

import multiWidgetService from './multiWidgetService';
import storageService from './storageService';

import VIDABOO_CONSTANTS from '../config/consts';

const service = {};
const privateState = {};
service.init = () => {
  service.setIsInWidget();
};
service.isEmptyHash = (obj) => {
  if (!obj) return true;
  if (Object.keys(obj).length === 0 && obj.constructor === Object) {
    return true;
  }
  return false;
};
service.hashMergeAToB = (hashA, hashB) => {
  // if conflict keys, overrides hashB items with hashA items.
  Object.keys(hashA).forEach(function (keyHashA) {
    hashB[keyHashA] = hashA[keyHashA];
  });
};
service.hashMergeAToB2 = (hashA, hashB) => {
  Object.keys(hashA).forEach(function (keyHashA) {
    hashB[keyHashA] = hashA[keyHashA];
  });
  return hashB;
};

service.capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};
service.assignThemeCustomizationValues = (widgetStyle = {}) => {
  const selectedTheme = VIDABOO_CONSTANTS.MAIN_THEME[widgetStyle.themePreset];

  widgetStyle.themeSecondaryFgColor =
    widgetStyle.themeSecondaryFgColor ?? selectedTheme.themeSecondaryFgColor;
  widgetStyle.themeSecondaryBgColor = service.getLinearGradient(
    widgetStyle.themeSecondaryBgColor ?? selectedTheme.themeSecondaryBgColor,
  );
  widgetStyle.themePrimaryFgColor =
    widgetStyle.themePrimaryFgColor ?? selectedTheme.themePrimaryFgColor;
  widgetStyle.themePrimaryBgColor =
    widgetStyle.themePrimaryBgColor ?? selectedTheme.themePrimaryBgColor;
};
service.assignWidgetCustomizationValues = (widgetListStyle, widgetStyle) => {
  const widgetBackgroundColor =
    widgetStyle.widgetBackgroundColor ?? widgetStyle.themeSecondaryBgColor;

  service.setCssVariable(
    '--et-widget-mask-background-color',
    service.getLinearGradient(widgetBackgroundColor),
  );
  let widgetMaskBgColorFirstColor = widgetBackgroundColor;
  if (service.getIsLinearGradient(widgetMaskBgColorFirstColor)) {
    widgetMaskBgColorFirstColor = service.getLinearGradientColorsArr(
      widgetMaskBgColorFirstColor,
    )[0];
  }
  service.setCssVariable(
    '--et-widget-mask-background-color-fc',
    widgetMaskBgColorFirstColor,
  );

  service.setCssVariable(
    '--et-widget-mask-text-color',
    service.getLinearGradient(
      widgetListStyle?.widgetMaskTextColor ?? widgetStyle.themeSecondaryFgColor,
    ),
  );

  service.assignTeamListCustomizationValues(widgetListStyle, widgetStyle);
};
service.assignTeamListCustomizationValues = (
  widgetListStyle = {},
  widgetStyle = {},
) => {
  service.setCssVariable(
    '--et-team-primary-background-color',
    widgetListStyle?.primaryBackgroundColor ?? widgetStyle.themePrimaryBgColor,
  );
  service.setCssVariable(
    '--et-team-secondary-background-color',
    service.getLinearGradient(
      widgetListStyle?.secondaryBackgroundColor ??
        widgetStyle.themeSecondaryBgColor,
    ),
  );
  service.setCssVariable(
    '--et-team-primary-text-color',
    widgetListStyle?.primaryTextColor ?? widgetStyle.themePrimaryFgColor,
  );
  service.setCssVariable(
    '--et-team-secondary-text-color',
    widgetListStyle?.secondaryTextColor ?? widgetStyle.themeSecondaryFgColor,
  );
  service.setCssVariable(
    '--et-team-cta-btn-primary-text-color',
    widgetListStyle?.ctaBtnTextColor ?? widgetStyle.themePrimaryFgColor,
  );

  const ctaBtnSecondaryTextColor =
    widgetListStyle?.ctaBtnSecondaryTextColor ??
    service.getNeutralColor(
      widgetStyle.themePrimaryFgColor,
      widgetStyle.themePrimaryBgColor,
      0.2,
    );
  service.setCssVariable(
    '--et-team-cta-btn-scnd-text-color',
    ctaBtnSecondaryTextColor,
  );

  service.setCssVariable(
    '--et-team-cta-btn-primary-background-color',
    widgetListStyle?.ctaBtnColor ?? widgetStyle.themePrimaryBgColor,
  );

  let teamPrimaryBorderColor =
    widgetStyle.themeSecondaryBgColor ?? widgetListStyle?.primaryTextColor;
  if (service.getIsLinearGradient(teamPrimaryBorderColor)) {
    teamPrimaryBorderColor = service.getLinearGradientColorsArr(
      teamPrimaryBorderColor,
    )[0];
  }
  service.setCssVariable(
    '--et-team-primary-border-color',
    service.hexToRgbA(teamPrimaryBorderColor),
  );
  service.setCssVariable(
    '--et-team-primary-border-color-hovered',
    service.hexToRgbA(teamPrimaryBorderColor, 0.9),
  );
};
service.assignMentorCustomizationValues = (
  mentorStyle = {},
  widgetStyle = {},
) => {
  const primaryBackgroundColor =
    mentorStyle?.primaryBackgroundColor ?? widgetStyle.themePrimaryBgColor;
  service.setCssVariable(
    '--et-mentor-primary-background-color',
    primaryBackgroundColor,
  );
  const primaryTextColor =
    mentorStyle?.primaryTextColor ?? widgetStyle.themePrimaryFgColor;
  service.setCssVariable('--et-mentor-primary-text-color', primaryTextColor);

  const secondaryTextColor =
    mentorStyle?.secondaryTextColor ??
    service.getNeutralColor(primaryTextColor, primaryBackgroundColor, 0.5);
  service.setCssVariable(
    '--et-mentor-secondary-text-color',
    secondaryTextColor,
  );
  const primaryBorderColor = service.getNeutralColor(
    secondaryTextColor,
    primaryBackgroundColor,
    0.6,
  );

  service.setCssVariable(
    '--et-mentor-primary-border-color',
    primaryBorderColor,
  );
  const primaryBorderColorShaded = service.getNeutralColor(
    secondaryTextColor,
    primaryBackgroundColor,
    0.8,
  );
  service.setCssVariable(
    '--et-mentor-primary-border-color-shaded',
    primaryBorderColorShaded,
  );

  const ctaBtnPrimaryBgColor = service.getLinearGradient(
    mentorStyle?.ctaBtnColor ?? widgetStyle.themeSecondaryBgColor,
  );
  service.setCssVariable(
    '--et-mentor-cta-btn-primary-background-color',
    ctaBtnPrimaryBgColor,
  );
  let ctaBtnPrimaryBgColorFirstColor =
    mentorStyle?.ctaBtnColor ?? widgetStyle.themeSecondaryBgColor;
  if (service.getIsLinearGradient(ctaBtnPrimaryBgColorFirstColor)) {
    ctaBtnPrimaryBgColorFirstColor = service.getLinearGradientColorsArr(
      ctaBtnPrimaryBgColorFirstColor,
    )[0];
  }
  service.setCssVariable(
    '--et-mentor-title-text-color',
    ctaBtnPrimaryBgColorFirstColor,
  );

  service.setCssVariable(
    '--et-mentor-cta-btn-primary-background-color-fc',
    ctaBtnPrimaryBgColorFirstColor,
  );
  const ctaBtnTextColor =
    mentorStyle?.ctaBtnTextColor ?? widgetStyle.themeSecondaryFgColor;
  service.setCssVariable(
    '--et-mentor-cta-primary-btn-text-color',
    ctaBtnTextColor,
  );
  // secondary call-to-action buttom BG should be built from primary colors and NOT CTA colors.
  const ctaBtnScndBgColor = utilsService.getIsPersonalPage()
    ? service.getNeutralColor(
        ctaBtnTextColor,
        ctaBtnPrimaryBgColorFirstColor,
        0.12,
      )
    : service.getNeutralColor(
        primaryBackgroundColor,
        ctaBtnPrimaryBgColorFirstColor,
        0.12,
      );
  service.setCssVariable(
    '--et-mentor-cta-btn-scnd-background-color',
    ctaBtnScndBgColor,
  );

  const ctaBtnScndBgColorShaded = service.getNeutralColor(
    primaryBackgroundColor,
    ctaBtnPrimaryBgColorFirstColor,
    0.06,
  );
  service.setCssVariable(
    '--et-mentor-cta-btn-scnd-background-color-shaded',
    ctaBtnScndBgColorShaded,
  );

  let mentorPeerBubbleBorderColor =
    mentorStyle?.peerBubbleBorderColor ?? widgetStyle.themeSecondaryBgColor;
  if (service.getIsLinearGradient(mentorPeerBubbleBorderColor)) {
    mentorPeerBubbleBorderColor = service.getLinearGradientColorsArr(
      mentorPeerBubbleBorderColor,
    )[0];
  }
  service.setCssVariable(
    '--et-mentor-peer-bubble-border-color',
    mentorPeerBubbleBorderColor,
  );
  service.setCssVariable(
    '--et-mentor-peer-bubble-border-thickness',
    (mentorStyle?.peerBubbleBorderThickness ??
      VIDABOO_CONSTANTS.MENTOR_CUSTOMIZATION_STYLING[
        'peerBubbleBorderThickness'
      ]) + 'px',
  );

  const peerBubbleFillColor = mentorStyle?.peerBubbleFillColor
    ? service.getLinearGradient(mentorStyle?.peerBubbleFillColor)
    : service.getNeutralColor(
        widgetStyle.themeSecondaryFgColor,
        widgetStyle.themeSecondaryBgColor,
        0.2,
      );
  service.setCssVariable(
    '--et-mentor-peer-bubble-fill-color',
    peerBubbleFillColor,
  );

  service.setCssVariable(
    '--et-mentor-peer-bubble-text-color',
    mentorStyle?.peerBubbleTextColor ?? widgetStyle.themePrimaryFgColor,
  );
  service.setCssVariable(
    '--et-mentor-my-bubble-border-color',
    mentorStyle?.myBubbleBorderColor ?? widgetStyle.themePrimaryBgColor,
  );
  service.setCssVariable(
    '--et-mentor-my-bubble-border-thickness',
    (mentorStyle?.myBubbleBorderThickness ??
      VIDABOO_CONSTANTS.MENTOR_CUSTOMIZATION_STYLING[
        'myBubbleBorderThickness'
      ]) + 'px',
  );
  service.setCssVariable(
    '--et-mentor-my-bubble-fill-color',
    utilsService.getLinearGradient(
      mentorStyle?.myBubbleFillColor ?? widgetStyle.themeSecondaryBgColor,
    ),
  );
  service.setCssVariable(
    '--et-mentor-my-bubble-text-color',
    mentorStyle?.myBubbleTextColor ?? widgetStyle.themeSecondaryFgColor,
  );

  let systemBubbleBorderColor =
    mentorStyle?.systemBubbleBorderColor ?? widgetStyle.themeSecondaryBgColor;
  if (service.getIsLinearGradient(systemBubbleBorderColor)) {
    systemBubbleBorderColor = service.getLinearGradientColorsArr(
      systemBubbleBorderColor,
    )[0];
  }
  service.setCssVariable(
    '--et-mentor-system-bubble-border-color',
    systemBubbleBorderColor,
  );
  service.setCssVariable(
    '--et-mentor-system-bubble-border-thickness',
    (mentorStyle?.systemBubbleBorderThickness ??
      VIDABOO_CONSTANTS.MENTOR_CUSTOMIZATION_STYLING[
        'systemBubbleBorderThickness'
      ]) + 'px',
  );
  service.setCssVariable(
    '--et-mentor-system-bubble-fill-color',
    service.getLinearGradient(
      mentorStyle?.systemBubbleFillColor ?? widgetStyle.themePrimaryBgColor,
    ),
  );
  service.setCssVariable(
    '--et-mentor-system-bubble-text-color',
    mentorStyle?.systemBubbleTextColor ?? widgetStyle.themePrimaryFgColor,
  );

  const inputFieldBubbleBorderColor =
    mentorStyle?.inputFieldBubbleBorderColor ??
    service.getNeutralColor(
      widgetStyle.themePrimaryBgColor,
      widgetStyle.themePrimaryFgColor,
      0.2,
    );
  service.setCssVariable(
    '--et-mentor-input-field-bubble-border-color',
    inputFieldBubbleBorderColor,
  );

  service.setCssVariable(
    '--et-mentor-input-field-bubble-border-thickness',
    (mentorStyle?.inputFieldBubbleBorderThickness ??
      VIDABOO_CONSTANTS.MENTOR_CUSTOMIZATION_STYLING[
        'inputFieldBubbleBorderThickness'
      ]) + 'px',
  );
  service.setCssVariable(
    '--et-mentor-input-field-bubble-fill-color',
    mentorStyle?.inputFieldBubbleFillColor ?? widgetStyle.themePrimaryBgColor,
  );
  service.setCssVariable(
    '--et-mentor-input-field-bubble-text-color',
    mentorStyle?.inputFieldBubbleTextColor ?? widgetStyle.themePrimaryFgColor,
  );
  service.setCssVariable(
    '--et-mentor-input-hazard-text-color',
    mentorStyle?.inputHazardTextColor ??
      VIDABOO_CONSTANTS.MENTOR_CUSTOMIZATION_STYLING.inputHazardTextColor,
  );
};

service.hashMergeAToB2 = (hash1, hash2) => {
  Object.keys(hash1).forEach(function (keyHash1) {
    hash2[keyHash1] = hash1[keyHash1];
  });
  return hash2;
};

service.loadScriptAsync = (scriptPath, htmlElement, id, dataObj, callback) => {
  const script = document.createElement('script');
  script.src = scriptPath;
  script.async = true;
  if (id) {
    const a = document.getElementById(id);
    if (a) return;
    script.id = id;
  }
  dataObj &&
    Object.keys(dataObj).forEach((key) => {
      script.setAttribute('data-' + key, dataObj[key]);
    });
  if (callback) {
    script.onload = callback;
  }
  htmlElement.appendChild(script);
};
service.itemExistsInArray = (item, array) => {
  if (!Array.isArray(array)) return false;
  return array.some(function (el) {
    return el === item;
  });
};

//todomcss : ok
service.isMobileDevice = () => {
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return true;
  }
  /* eslint-disable */
  var isMobile = false;
  (function (a, b) {
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
        a,
      ) ||
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4),
      )
    )
      isMobile = true;
  })(navigator.userAgent || navigator.vendor || window.opera);
  return isMobile;
  /* eslint-enable */

  // var check = false;
  // (function (a) {
  //   if (
  //     /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
  //       a,
  //     ) ||
  //     /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
  //       a.substr(0, 4),
  //     )
  //   )
  //     check = true;
  // })(window.navigator.userAgent || window.navigator.vendor || window.opera);
  // return check;
};

service.getIsInSafariBrowser = () => {
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return false;
  }
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

service.getIsInFirefoxBrowser = () => {
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return false;
  }
  return /firefox/i.test(navigator.userAgent);
};

service.handleMarketingParametersServiceRequest = (action) => {
  if (service.isLocalStorageSupported()) {
    let marketingParamsDate = storageService.getItem('marUpdate');
    if (marketingParamsDate) {
      const marketingParamsDateHash = JSON.parse(atob(marketingParamsDate));

      const marketingDate = moment(
        new Date(marketingParamsDateHash[action] ?? 0),
        'DD-MM-YYYY',
      );
      const nowDate = moment(new Date(), 'DD-MM-YYYY');
      var diff = nowDate.diff(marketingDate, 'days');
      if (diff > 7) {
        marketingParamsDateHash[action] = new Date();
        storageService.setItem(
          'marUpdate',
          btoa(JSON.stringify(marketingParamsDateHash)),
        );
        return true;
      } else {
        return false;
      }
    } else {
      // console.debug('new Date()', new Date());
      const marketingParamsDateHash = {};
      marketingParamsDateHash[action] = new Date();
      storageService.setItem(
        'marUpdate',
        btoa(JSON.stringify(marketingParamsDateHash)),
      );
      return true;
    }
  }
  return false;
};
service.localStorageSupported_ = null;
service.isLocalStorageSupported = () => {
  try {
    if (service.localStorageSupported_ !== null)
      return service.localStorageSupported_;
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('feature_test', 'yes');
        if (localStorage.getItem('feature_test') === 'yes') {
          localStorage.removeItem('feature_test');
          service.localStorageSupported_ = true;
          return true;
          // localStorage is enabled
        } else {
          service.localStorageSupported_ = false;
          return false;
          // localStorage is disabled
        }
      } catch (e) {
        service.localStorageSupported_ = false;
        return false;
        // localStorage is disabled
      }
    } else {
      // localStorage is not available
      service.localStorageSupported_ = false;
      return false;
    }
  } catch (error) {
    // console.debug("error");
  }
};
service.getCurrentLocation = () => {
  return VIDABOO_CONSTANTS.NATIVE_APP ? null : window.location.pathname;
};
service.getAbsLocation = () => {
  return VIDABOO_CONSTANTS.NATIVE_APP ? null : window.location.href;
};
service.getHost = () => {
  // returns host including port. For example: my-ip.yuvidio.com:3000. Does not include protocol
  return VIDABOO_CONSTANTS.NATIVE_APP ? null : window.location.host;
};
service.setIsInWidget = () => {
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return;
  }
  if (
    window.location.pathname.match('/expertWidget/') !== null ||
    window.location.pathname.match('/multiWidget/') !== null
  ) {
    // always on client.consolto.com
    service.rootScope_expertWidget = true;
    service.rootScope_expert = false;
  } else if (window.location.pathname.match('/expert/') !== null) {
    // https://client.consolto.com/expert/profilename ,
    // https://client.consolto.com/clickntalk/profilename ,
    // https://client.consolto.com/c/profilename,

    // ILAN Needs to implement the following cnames:

    // https://clknt.lk/profilename

    // https://clickntalk.consolto.com/profilename
    // https://clickntalk.consolto.com/m/wpofiusadf
    // https://clickntalk.consolto.com/media/aslkdfja;sdf

    // Customer CNAMEs types:
    // https://justcallme.mydomain.com/clickntalk/profilename
    // https://justcallme.mydomain.com/c/profilename
    // https://clickntalk.mydomain.com/profilename

    service.rootScope_expert = true;
    service.rootScope_expertWidget = false;
  } else if (window.location.pathname.match('/expertDir/') !== null) {
    service.rootScope_expert = false;
    service.rootScope_expertWidget = false;
  } else {
    service.rootScope_expert = false;
    service.rootScope_expertWidget = false;
  }
  if (
    window.location.pathname.match('/media/') !== null ||
    window.location.pathname.match('/cs/') !== null ||
    window.location.pathname.match('/redeemGift/') !== null ||
    window.location.pathname.match('/m/') !== null ||
    window.location.pathname.match('/mb/') !== null ||
    window.location.pathname.match('/expertDir/') !== null
  )
    service.isClientExtendedPages = true;
  // console.debug(service.isClientExtendedPages);
};

service.getIsInWidget = () => service.rootScope_expertWidget;

service.getIsClient = () =>
  service.rootScope_expert ||
  service.rootScope_expertWidget ||
  service.isClientExtendedPages;
service.getIsPersonalPage = () => service.rootScope_expert;

service.getIsInApp = () => {
  // includes the backoffice as well as login pages etc.
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return true;
  }
  if (document.location.pathname.match(VIDABOO_CONSTANTS.APP_PAGE) !== null) {
    // todo: JASHAN;
    return true;
  }
  return false;
};
service.getIsInExpertHome = () => {
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return;
  }
  if (document.location.pathname.match('/expertHome') !== null) {
    return true;
  }
  return false;
};
service.getIsEmbeddedPersonalPage = () => {
  // Embedded SSO
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return;
  }
  if (
    (window.location.ancestorOrigins?.length > 0 || window !== window.top) && //ancestorOrigins doesn't exist in firefox, for that we need to use window
    window.location.pathname.match('/expert/')
  ) {
    return true;
  }
  return false;
};
service.getIsEmbeddedExpertHome = () => {
  // Embedded SSO
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return;
  }

  if (
    window.location.ancestorOrigins?.length > 0 &&
    window.location.hostname.match('app.consolto.com')
  ) {
    return true;
  }
  return false;
};
service.getIsInCallInPopup = () => {
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    return;
  }
  if (window.location.pathname.match('/callInPopup/') !== null) return true;
  return false;
};
service.getIsInExpertHomeChatCenter = () => {
  if (window.location.pathname.match('/expertHome/chatCenter') !== null) {
    return true;
  }
  return false;
};
// // return false if this is mentor
// service.getIsClient = () =>
//   service.rootScope_expert || service.rootScope_expertWidget;

privateState.fallbackCopyTextToClipboard = (text) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    const msg = successful ? 'successful' : 'unsuccessful';
    console.debug('Fallback: Copying text command was ' + msg);
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err);
  }

  document.body.removeChild(textArea);
};
service.copyTextToClipboard = (text) => {
  if (VIDABOO_CONSTANTS.NATIVE_APP) {
    // this case is handled in utilService.native.js
  } else {
    if (!navigator.clipboard) {
      privateState.fallbackCopyTextToClipboard(text);
      return;
    }
    navigator.clipboard.writeText(text).then(
      function () {
        console.debug('Async: Copying to clipboard was successful!');
      },
      function (err) {
        console.error('Async: Could not copy text: ', err);
      },
    );
  }
};

service.convertPageRegexArrToString = (pageRegexArr) => {
  var pageRegexesStr = '';
  var firstIteration = true;
  if (
    pageRegexArr === null ||
    pageRegexArr === undefined ||
    pageRegexArr.length < 1
  )
    return pageRegexesStr;
  pageRegexArr.forEach(function (pageRegex) {
    if (firstIteration) {
      pageRegexesStr = pageRegex;
      firstIteration = false;
    } else {
      pageRegexesStr = pageRegexesStr + ',' + pageRegex;
    }
  });
  // console.debug(pageRegexesStr);
  return pageRegexesStr;
};
service.convertPageRegexStringToArr = (pageRegexStr) => {
  if (pageRegexStr === '') return [];
  pageRegexStr = pageRegexStr.replace(/\s/g, '');
  var pageRegexArr = pageRegexStr.split(',');
  for (var index = 0; index < pageRegexArr.length; index++) {
    if (pageRegexArr[index] === '') {
      pageRegexArr.splice(index, 1);
      index--;
    }
  }

  return pageRegexArr;
};

service.shouldRenderAsHTML = (message) => {
  return /<a[\s]+([^>]+)>((?:.(?!<\/a>))*.)<\/a>/g.test(message);
  // &&  !/(?:<iframe[^>]*)(?:(?:\/>)|(?:>.*?))/gm.test(message)
};

service.checkIfHTML = (message) => {
  return VIDABOO_CONSTANTS.VALIDATION_PATTERNS.HTML_START_OR_END_ELEMENT.test(
    message,
  );
};
service.checkIfNeedToSendPeerInfo = (mentorId) => {
  if (service.isLocalStorageSupported()) {
    const needSendPeerClientInfoStr = storageService.getItem(
      'needSendPeerClientInfo',
    );
    if (needSendPeerClientInfoStr) {
      const needSendPeerClientInfoHash = JSON.parse(needSendPeerClientInfoStr);
      if (needSendPeerClientInfoHash[mentorId]) {
        return needSendPeerClientInfoHash[mentorId];
      }
      return false;
    } else return false;
  } else return false;
};
service.updateNeedSendPeerInfo = (flag, mentorId) => {
  const needSendPeerClientInfoStr = storageService.getItem(
    'needSendPeerClientInfo',
  );
  let needSendPeerClientInfoHash = {};
  if (needSendPeerClientInfoStr)
    needSendPeerClientInfoHash = JSON.parse(needSendPeerClientInfoStr);
  needSendPeerClientInfoHash[mentorId] = flag;
  storageService.setItem(
    'needSendPeerClientInfo',
    JSON.stringify(needSendPeerClientInfoHash),
  );
};
privateState.normalizePeerClientInfo = (mentorOwnerId, widgetDto) => {
  // normalizing the data in the localStorage so that it is based on widgetId instead of mentorOwnerId
  let peerClientInfo = storageService.get('peerClientInfo');
  if (peerClientInfo) {
    const extendedMentorDto = multiWidgetService.getExMentorDtoByMentorOwnerId(
      mentorOwnerId,
      widgetDto,
    );
    const mentorId = extendedMentorDto.mentorDto.mentorId;
    if (
      extendedMentorDto &&
      extendedMentorDto.mentorDto.mentorOwnerType ===
        VIDABOO_CONSTANTS.MENTOR_OWNER_TYPE.PERSONAL
    ) {
      peerClientInfo = JSON.parse(peerClientInfo);
      if (peerClientInfo[mentorOwnerId]) {
        const widgetId = widgetDto.widgetId;
        // save according to widgetId
        peerClientInfo[widgetId] = {};
        if (peerClientInfo[mentorOwnerId].name) {
          peerClientInfo[widgetId].name = peerClientInfo[mentorOwnerId].name;
        }
        if (peerClientInfo[mentorOwnerId].email) {
          peerClientInfo[widgetId].email = peerClientInfo[mentorOwnerId].email;
        }
        if (peerClientInfo[mentorOwnerId].phone_number) {
          peerClientInfo[widgetId].phone_number =
            peerClientInfo[mentorOwnerId].phone_number;
        }
        service.updateNeedSendPeerInfo(true, mentorId);
        delete peerClientInfo[mentorOwnerId];
        storageService.setItem(
          'peerClientInfo',
          JSON.stringify(peerClientInfo),
        );
      }
    }
  }
};
service.savePeerClientInfo = (peerInfo, widgetDto) => {
  // saving and updating needSendPeerClientInfo. Returns false or the saved info object itself.
  const {
    cFirstName,
    cLastName,
    cPhone,
    cEmail,
    mentorOwnerId, // mentorOwnerId is used only for mormalizing the old peerClientInfo
    mentorId,
  } = peerInfo;
  let sendInfoMessage = false;
  const widgetId = widgetDto.widgetId;

  privateState.normalizePeerClientInfo(mentorOwnerId, widgetDto);

  let peerClientInfo = storageService.get('peerClientInfo');
  if (peerClientInfo) {
    peerClientInfo = JSON.parse(peerClientInfo);
    if (!peerClientInfo[widgetId]) {
      peerClientInfo[widgetId] = {};
      peerClientInfo[widgetId].senderUserId = 'GUEST';
      peerClientInfo[widgetId].subject = 'Email from your potential client.';
      sendInfoMessage = true;
    }
  } else {
    peerClientInfo = {};
    peerClientInfo[widgetId] = {};
    peerClientInfo[widgetId].senderUserId = 'GUEST';
    peerClientInfo[widgetId].subject = 'Email from your potential client.';
    sendInfoMessage = true;
  }

  if (cEmail) {
    if (peerClientInfo[widgetId].email !== cEmail && !sendInfoMessage) {
      sendInfoMessage = true;
    }
    peerClientInfo[widgetId].email = cEmail;
  }
  if (cFirstName || cLastName) {
    const fullName = (cFirstName ?? '') + (cLastName ? ' ' + cLastName : '');
    if (peerClientInfo[widgetId].name !== fullName && !sendInfoMessage) {
      sendInfoMessage = true;
    }
    peerClientInfo[widgetId].name = fullName;
  }
  if (cPhone) {
    if (peerClientInfo[widgetId].phone_number !== cPhone && !sendInfoMessage) {
      sendInfoMessage = true;
    }
    peerClientInfo[widgetId].phone_number = cPhone;
  }
  storageService.setItem('peerClientInfo', JSON.stringify(peerClientInfo));

  if (sendInfoMessage) {
    // saving the flag only if data has changed.
    Object.keys({
      ...widgetDto.mentorsControlMapDto,
      ...(widgetDto.teamMentorsMapDto ?? {}),
    }).forEach((mId) => {
      service.updateNeedSendPeerInfo(true, mId);
    });
  } else {
    const needSendPeerClientInfoStr = storageService.getItem(
      'needSendPeerClientInfo',
    );
    if (needSendPeerClientInfoStr) {
      const needSendPeerClientInfoHash = JSON.parse(needSendPeerClientInfoStr);
      sendInfoMessage = needSendPeerClientInfoHash[mentorId];
    }
  }

  return sendInfoMessage;
};

service.shouldCollectPeerInfo = (mentorOwnerId, widgetDto) => {
  // returns true if we should collect customer info.
  let isInfoShouldBeCollected = true;
  if (service.isLocalStorageSupported()) {
    let peerClientInfo = service.getPeerClientInfo(mentorOwnerId, widgetDto);
    if (peerClientInfo) {
      isInfoShouldBeCollected = false;
    }
  } else {
    isInfoShouldBeCollected = false;
  }
  return isInfoShouldBeCollected;
};

service.getPeerClientInfo = (mentorOwnerId, widgetDto) => {
  // mentorOwnerId is used here only for normalizing
  if (service.isLocalStorageSupported()) {
    privateState.normalizePeerClientInfo(mentorOwnerId, widgetDto);
    let peerClientInfo = storageService.get('peerClientInfo');
    const widgetId = widgetDto.widgetId;
    if (peerClientInfo) {
      peerClientInfo = JSON.parse(peerClientInfo);
      return peerClientInfo[widgetId];
    } else return null;
  } else return null;
};
service.languagePatch = (userId, language) => {
  if (language === 'ESPANOL') {
    storageService.setItem(
      'userWidgetLanguage',
      JSON.stringify({[userId]: 'SPANISH'}),
    );
    return 'SPANISH';
  } else {
    return language;
  }
};
service.backOfficeUiLanguagePatch = (language) => {
  return language === 'ESPANOL' ? 'SPANISH' : language;
};

service.saveTeamOneOnOneRoomInfo = (teamOneOnOneRoomInfo) => {
  // teamOneOnOneRoomInfo =  widgetId: { selectedMentorId, roomCreationInitiator }
  storageService.setItem(
    VIDABOO_CONSTANTS.LOCAL_STORAGE_ITEMS.TEAM_ONE_ON_ONE_ROOM_INFO,
    JSON.stringify(teamOneOnOneRoomInfo),
  );
};

service.getTeamOneOnOneRoomInfo = (widgetId) => {
  let roomInfo = {};
  let teamOneOnOneRoomsInfo = storageService.getItem(
    VIDABOO_CONSTANTS.LOCAL_STORAGE_ITEMS.TEAM_ONE_ON_ONE_ROOM_INFO,
  );
  if (teamOneOnOneRoomsInfo) {
    teamOneOnOneRoomsInfo = JSON.parse(teamOneOnOneRoomsInfo);
    if (teamOneOnOneRoomsInfo[widgetId]) {
      roomInfo = teamOneOnOneRoomsInfo[widgetId];
    }
  }
  return roomInfo;
};

service.removeTeamOneOnOneRoomInfo = () => {
  const teamOneOnOneRoomsInfo = storageService.getItem(
    VIDABOO_CONSTANTS.LOCAL_STORAGE_ITEMS.TEAM_ONE_ON_ONE_ROOM_INFO,
  );
  if (teamOneOnOneRoomsInfo) {
    storageService.removeItem(
      VIDABOO_CONSTANTS.LOCAL_STORAGE_ITEMS.TEAM_ONE_ON_ONE_ROOM_INFO,
    );
  }
};

service.getBillingPlanDtoFromBillingPlanDtos = (billingPlanDtos) => {
  if (!billingPlanDtos) return null;
  const billingPlans = [];
  billingPlanDtos.forEach((plan) => {
    if (plan.planType === 'SUBSCRIPTION') {
      billingPlans.push(plan);
    }
  });
  if (billingPlans.length === 0) return null;
  const billingPlanDto = billingPlans[0];
  return billingPlanDto;
};

service.getAddonPlanDtosFromBillingPlanDtos = (billingPlanDtos) => {
  if (!billingPlanDtos) return [];
  const addons = [];
  billingPlanDtos.forEach((plan) => {
    if (plan.planType === 'ADDON') {
      addons.push(plan);
    }
  });
  return addons;
};

service.getNotificationPermission = () => {
  return new Promise((resolve, reject) => {
    if (!('Notification' in window)) {
      reject({message: 'This browser does not support desktop notification'});
    } else if (Notification.permission !== 'granted') {
      Notification.requestPermission(function (permission) {
        if (!('permission' in Notification)) {
          Notification.permission = permission;
        }
        if (permission === 'granted') {
          resolve({message: 'Permission granted'});
        } else {
          reject({message: `Permission is ${Notification.permission}`});
        }
      });
    } else {
      if (Notification.permission === 'granted') {
        resolve({message: `Permission is ${Notification.permission}`});
      } else {
        reject({message: `Permission is ${Notification.permission}`});
      }
    }
  });
};
service.setCssVariable = (varName, value) => {
  document.querySelector(':root').style.setProperty(varName, value);
};

service.hexToRgbA = (hex, opacity) => {
  var c;
  if (!opacity) opacity = 0.1;
  let newCode = hex;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(newCode)) {
    c = newCode?.substring(1).split('');
    if (c.length === 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = '0x' + c.join('');
    const r =
      'rgba(' +
      [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',') +
      ',' +
      opacity +
      ')';
    return r;
  } else {
    return newCode?.replace(/[^,]+(?=\))/, opacity);
  }
};
service.getRgbFromRgba = (rgbaString) => {
  const match = rgbaString.match(
    /rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*\d*\.?\d*?)?\s*\)/,
  );
  return match ? `${match[1]}, ${match[2]}, ${match[3]}` : null;
};
service.getIsLinearGradient = (gradientColorsInfo) => {
  return (
    (gradientColorsInfo?.length > 1 && Array.isArray(gradientColorsInfo)) ||
    service.getIsLinearGradientStr(gradientColorsInfo)
  );
};
service.getIsLinearGradientStr = (gradientColorsStr) => {
  return gradientColorsStr?.includes('linear-gradient');
};
service.getLinearGradientColorsArr = (gradientColorsInfo) => {
  let hexColors = [];
  if (service.getIsLinearGradient(gradientColorsInfo)) {
    if (service.getIsLinearGradientStr(gradientColorsInfo)) {
      hexColors = gradientColorsInfo.match(/#[a-fA-F0-9]{6}|#[a-fA-F0-9]{3}/g);
    } else {
      hexColors = gradientColorsInfo;
    }
  }
  return hexColors;
};
service.getLinearGradient = (
  gradientColors = [],
  direction = VIDABOO_CONSTANTS.WIDGET_GRADIENT_DIRECTION,
) => {
  if (Array.isArray(gradientColors)) {
    let gradientColorsStr = gradientColors
      // .map((gradientColor) => service.hexToRgbA(gradientColor, 1))
      .join(', ');
    return gradientColors.length === 1
      ? gradientColorsStr
      : `linear-gradient(${direction}, ${gradientColorsStr})`;
  } else {
    return gradientColors;
  }
};
const rgbToHex = ({r, g, b}) => {
  // Convert RGB to hex
  const toHex = (value) => value.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};
const hexToRgb = (hex) => {
  // Convert hex colors to RGB
  hex = hex.replace('#', '');
  return {
    r: parseInt(hex.substring(0, 2), 16),
    g: parseInt(hex.substring(2, 4), 16),
    b: parseInt(hex.substring(4, 6), 16),
  };
};
service.getColorHexByCssClass = (cssClass) => {
  return getComputedStyle(document.documentElement).getPropertyValue(cssClass);
};
service.getNeutralColor = (color1, color2, percentage = 0.2) => {
  let rgb1, rgb2;

  // Get RGB values of the colors
  if (service.getIsLinearGradient(color1)) {
    rgb1 = hexToRgb(service.getLinearGradientColorsArr(color1)[0]);
  } else {
    rgb1 = hexToRgb(color1);
  }
  if (service.getIsLinearGradient(color2)) {
    rgb2 = hexToRgb(service.getLinearGradientColorsArr(color2)[0]);
  } else {
    rgb2 = hexToRgb(color2);
  }

  // Ensure the percentage is clamped between 0 and 1
  percentage = Math.min(Math.max(percentage, 0), 1);

  // Calculate the interpolated RGB values
  const blendedRgb = {
    r: Math.round(rgb1.r + (rgb2.r - rgb1.r) * percentage),
    g: Math.round(rgb1.g + (rgb2.g - rgb1.g) * percentage),
    b: Math.round(rgb1.b + (rgb2.b - rgb1.b) * percentage),
  };

  // Convert the result back to hex
  return rgbToHex(blendedRgb);
};
service.parseStyleObject = (styleObj) => {
  for (let [key, value] of Object.entries(styleObj ?? {})) {
    if (
      typeof value === 'string' &&
      (value.startsWith('"') || value.startsWith('['))
    ) {
      try {
        styleObj[key] = JSON.parse(value);
      } catch (err) {
        console.error(`Error parsing value for ${key}:`, err);
      }
    }
  }
};
service.stringifyStyleObject = (styleObj) => {
  for (let [key, value] of Object.entries(styleObj ?? {})) {
    if (
      value &&
      typeof value !== 'string' &&
      !VIDABOO_CONSTANTS.STYLE_KEYS_EXCLUDED_FROM_STRINGIFY.includes(key)
    ) {
      styleObj[key] = JSON.stringify(value);
    }
  }
};
service.stringifyWidgetStyles = (widgetDto) => {
  service.stringifyStyleObject(widgetDto.widgetAppCtrlDto.style);
  Object.values(widgetDto.mentorsControlMapDto ?? {}).forEach(
    (widgetMentorCtrlDto) => {
      const mentorStyle =
        widgetMentorCtrlDto.extendedMentorDto.mentorDto.appControlDto.style;
      service.stringifyStyleObject(mentorStyle);
    },
  );
};
service.getSnippetCode = ({widgetId, version = '0.5'}) => {
  let jsURL = VIDABOO_CONSTANTS.CLIENT_PAGE;
  let testCode = 'false';
  if (VIDABOO_CONSTANTS.IFRAME_TEST === true) {
    testCode = 'true';
    jsURL = 'https://' + service.getHost();
  }
  return `<script>window.addEventListener('load', function () { var el = document.createElement('script'); el.setAttribute('src', '${jsURL}/iframeApp/iframeApp.js'); el.id = 'et-iframe'; el.async = true; el.setAttribute('data-widgetId', '${widgetId}'); el.setAttribute('data-version', ${version}); el.setAttribute('data-test', ${testCode}); document.body.appendChild(el); }); </script>`;
};

service.goToMessage = (chatMessageId) => {
  const element = document.getElementById(chatMessageId);
  if (!element) return;
  const currentHighlightedDiv = chatMessageId;
  localStorage.setItem('currentSelectedMedia', currentHighlightedDiv);

  document
    .querySelectorAll('.et-message-highlighted')
    .forEach((el) => el.classList.remove('et-message-highlighted'));

  const targetElement =
    element.querySelector('.et-session-instance-chat-container') ||
    element.querySelector('.et-n-drmb-ind-left') ||
    element.querySelector('.et-n-drmb-ind-right') ||
    element.querySelector('.et-n-file-msg-link--left') ||
    element.querySelector('.et-n-file-msg-link--right') ||
    element.querySelector('.et-n-file-msg-image-left') ||
    element.querySelector('.et-n-file-msg-image-right') ||
    element;

  targetElement.classList.add('et-message-highlighted');

  element.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};

service.debounce = (func, timeout = 1000) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeout);
  };
};

service.getSelectedWidgetAppType = (widgetAppType, actionClickData) => {
  //This method will return one of the VIDABOO_CONSTANTS.WIDGET_APP_TYPES type
  if (!service.isEmptyHash(actionClickData)) {
    if (
      actionClickData.bfi ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.AUDIO_CALL ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.VIDEO_CALL ||
      actionClickData.etClickType === 'start-call' ||
      actionClickData.etClickType === 'start-audio-call'
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.WEBCALL;
    } else if (
      actionClickData.etClickType === 'start-chat' ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.TEXT_CHAT
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.TEXT_CHAT;
    } else if (
      actionClickData.etClickType === 'start-scheduling' ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.SCHEDULING
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.SCHEDULE_MEETING;
    } else if (
      actionClickData.etClickType === 'start-form' ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.CONTACT_FORM
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.INFO_COLLECTOR;
    } else if (
      actionClickData.etClickType === 'start-fb-messenger' ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.FB_MESSENGER
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.FB_MESSENGER;
    } else if (
      actionClickData.etClickType === 'start-whatsapp' ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.WHATSAPP
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.WHATSAPP;
    } else if (
      actionClickData.etClickType === 'start-custom-link' ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.CUSTOM_LINK
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.CUSTOM_LINK;
    } else if (
      actionClickData.etClickType === 'start-phonecall' ||
      actionClickData.etConsoltoStart ===
        VIDABOO_CONSTANTS.TRIGGER_ACTIONS.PHONE_CALL
    ) {
      return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.PHONE_CALL;
    }
  } else {
    switch (widgetAppType) {
      case 'videocall':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.WEBCALL;
      case 'textchat':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.TEXT_CHAT;
      case 'audiocall':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.WEBCALL;
      case 'scheduling':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.SCHEDULE_MEETING;
      case 'form':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.INFO_COLLECTOR;
      case 'fbmessenger':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.FB_MESSENGER;
      case 'whatsapp':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.WHATSAPP;
      case 'customlink':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.CUSTOM_LINK;
      case 'phonecall':
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.PHONE_CALL;
      default:
        return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.NONE;
    }
  }
  return VIDABOO_CONSTANTS.WIDGET_APP_TYPES.NONE;
};
service.getEtClickTypeByWidgetAppType = (etClickType) => {
  switch (etClickType) {
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.WEBCALL:
      return 'start-audio-call';
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.TEXT_CHAT:
      return 'start-chat';
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.SCHEDULE_MEETING:
      return 'start-scheduling';
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.INFO_COLLECTOR:
      return 'start-form';
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.FB_MESSENGER:
      return 'start-fb-messenger';
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.WHATSAPP:
      return 'start-whatsapp';
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.CUSTOM_LINK:
      return 'start-custom-link';
    case VIDABOO_CONSTANTS.WIDGET_APP_TYPES.PHONE_CALL:
      return 'start-phonecall';
    default:
      return null;
  }
};
service.shuffleArray = (array) => {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle...
  while (currentIndex !== 0) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
};
service.getCurrencySymbol = (currency) => {
  switch (currency) {
    case VIDABOO_CONSTANTS.PAYMENT_TRANSACTION_CURRENCIES.USD:
      return '$';
    case VIDABOO_CONSTANTS.PAYMENT_TRANSACTION_CURRENCIES.EUR:
      return '€';
    case VIDABOO_CONSTANTS.PAYMENT_TRANSACTION_CURRENCIES.GBP:
      return '£';
    default:
      return '₪';
  }
};

service.getToastObj = ({
  id,
  isSticky = false,
  shouldRemoveToastAutomatically = true,
  toastTitle,
  toastType,
  toastLongDuration = false,
  toastCustomBtnTxt,
  toastCustomBtnAction,
  toastExtraData,
}) => {
  let toastObj = {
    isSticky,
    shouldRemoveToastAutomatically,
    toastId: id ?? uuidv4(),
    toastTitle,
  };
  if (toastType) {
    toastObj.toastType = toastType;
  }
  if (toastExtraData) {
    toastObj.toastExtraData = toastExtraData;
  }
  if (toastLongDuration) {
    toastObj.toastLongDuration = toastLongDuration;
  }
  if (toastCustomBtnTxt) {
    toastObj.toastCustomBtnTxt = toastCustomBtnTxt;
  }
  if (toastCustomBtnAction) {
    toastObj.toastCustomBtnAction = toastCustomBtnAction;
  }
  return toastObj;
};

service.getAssistantName = (assistantName) => {
  return assistantName
    ? `${assistantName} AI`
    : VIDABOO_CONSTANTS.AI_ASSISTANT_NAME;
};

service.getClickNTalkPageDomainUrl = (hostname) => {
  return hostname
    ? 'https://' + hostname
    : VIDABOO_CONSTANTS.CLIENT_CLICKNTALK_DOMAIN;
};

service.getApiErrorMessage = (errorInfo) => {
  if (Array.isArray(errorInfo)) {
    return errorInfo[0];
  } else {
    return errorInfo;
  }
};

service.handleOutsideAsyncBug = (func) => {
  //linked to bug : https://consolto.atlassian.net/browse/CFW-1574
  setTimeout(func, 0);
};

service.getPhotoDtoAndIdxByPhotoAndObjectType = (
  photoDtos,
  photoType,
  objectType,
) => {
  const photoDtoIdx = photoDtos?.findIndex(
    (photoDto) =>
      photoDto.photoType === photoType && photoDto.objectType === objectType,
  );
  return {
    photoDto: photoDtos && photoDtos[photoDtoIdx],
    photoDtoIdxInArr: photoDtoIdx,
  };
};

service.getPhotoManipulatedUrl = (photoDto, manipulationString) => {
  if (photoDto) {
    const imageOrVideo =
      photoDto.photoType === VIDABOO_CONSTANTS.PHOTO_TYPES.VIDEO_BACKGROUND
        ? 'video'
        : 'image';
    const manipulatedPhotoUrl = `https://res.cloudinary.com/${photoDto.cloudName}/${imageOrVideo}/upload/${manipulationString}/${photoDto.publicId}.${photoDto.format}`;
    return manipulatedPhotoUrl;
  }
  return null;
};

service.isCodeDisabled = (userId) => {
  return userId === '5aa5387b11c71f10107a98db';
};

service.isFeatureVisible = (userId) => {
  return [
    '5aa5387b11c71f10107a98db',
    '67b30823e292912365c99c83',
    '67e69111948b7a68f223d589',
    '6662fbc6fc170b5cfe4e653c',
    '5a9fc28211c71f10107a98c6',
  ].includes(userId);
};

service.getApiErrMsg = (errType, errCode, errMsg = 'Failed to update') => {
  return VIDABOO_CONSTANTS.ERROR_CODES[errType] &&
    VIDABOO_CONSTANTS.ERROR_CODES[errType][errCode]
    ? VIDABOO_CONSTANTS.ERROR_CODES[errType][errCode]
    : errMsg;
};

service.logApiResponse = (api, response) => {
  if (response.success || response.status === 'success') {
    console.debug('%c%s', 'color: #11d200;', api, ': ', response);
  } else {
    console.error(api, ': ', response);
  }
};

service.logFuncExecutionTime = (fn) => {
  return function (...args) {
    const startTime = performance.now();
    const result = fn(...args);
    const endTime = performance.now();
    const timeTaken = endTime - startTime;
    console.debug(
      '%c%s',
      'color: #f5f902;',
      `Execution time for ${fn.name}: ${timeTaken} milliseconds`,
    );
    return result;
  };
};

service.logAsyncFuncExecutionTime = (fn) => {
  return async function (...args) {
    const startTime = performance.now();
    const result = await fn(...args);
    const endTime = performance.now();
    const timeTaken = endTime - startTime;
    console.debug(
      '%c%s',
      'color: #f5f902;',
      `Execution time for ${fn.name}: ${timeTaken} milliseconds`,
    );
    return result;
  };
};

service.getKBRFileTypeExtensionsArr = () => {
  return Object.values(VIDABOO_CONSTANTS.KBR_FILE_TYPES).map(
    (fileType) => '.' + fileType,
  );
};

service.getKBRFileTypeExtensionsStr = () => {
  return Object.keys(VIDABOO_CONSTANTS.KBR_FILE_TYPES).join('/');
};

service.getFormattedId = (idPrefix, idSuffixs = []) => {
  let formattedId = '';
  const idSuffixsStr =
    typeof idSuffixs === 'string' ? idSuffixs : (idSuffixs?.join('_') ?? '');
  switch (idPrefix) {
    case VIDABOO_CONSTANTS.ID_PREFIX.CHATBOX:
      formattedId = VIDABOO_CONSTANTS.ID_PREFIX.CHATBOX + '-' + idSuffixsStr;
      break;
    case VIDABOO_CONSTANTS.ID_PREFIX.SITE_GUEST:
      formattedId = idSuffixsStr;
      break;
  }
  return formattedId;
};

service.getTextByChatSubType = (chatSubType, messageText) => {
  switch (chatSubType) {
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.INFO_MESSAGE:
      return 'Inbox Message';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.NOTES_MESSAGE:
      return 'Personal Note';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.VIDEOCHAT_MESSAGE:
      return 'Video Call';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.TEXT_MESSAGE:
      return messageText ?? 'Text message';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.SCHEDULE_MEETING:
      return 'Schedule appointment';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.MESSENGER_MESSAGE:
      return 'Facebook';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.WHATSAPP_MESSAGE:
      return 'Whatsapp';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.AUDIO_MESSAGE:
      return 'Audio message';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.FILE_MESSAGE:
      return 'File transfer';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.PHONE_MESSAGE:
      return 'Phone call';
    case VIDABOO_CONSTANTS.CHAT_SUBTYPES.MONEY_TRANSACTION:
      return 'Money transfer';
    default:
      return '';
  }
};

service.getWidgetMaskScaleFactor = (widgetSize) => {
  if (widgetSize === VIDABOO_CONSTANTS.WIDGET_STYLE_SIZE.LARGE) {
    return 1.2;
  } else if (widgetSize === VIDABOO_CONSTANTS.WIDGET_STYLE_SIZE.SMALL) {
    return 0.8;
  } else {
    return 1.0;
  }
};

service.getWidgetMaskTransformOrigin = (widgetLocation) => {
  if (widgetLocation === VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_LEFT) {
    return 'bottom left';
  } else if (
    widgetLocation === VIDABOO_CONSTANTS.WIDGET_LOCATION.BOTTOM_CENTER
  ) {
    return '100% 100%';
  }
  return 'bottom right';
};

service.getWidgetMaskDimension = (widgetMaskType, widgetMaskText) => {
  // This function will return the height and width of mask including the shadow space. But this function will not consider the positioning offsets (left, right space required according to website. That is handled in postMessageService.setWidgetLocation)
  let {height, width} = VIDABOO_CONSTANTS.WIDGET_MASK_DIMENSION.LOGO;
  if (VIDABOO_CONSTANTS.WIDGET_MASK_DIMENSION[widgetMaskType]) {
    ({height, width} = VIDABOO_CONSTANTS.WIDGET_MASK_DIMENSION[widgetMaskType]);
  } else {
    console.debug('Unsupported mask type: ', widgetMaskType);
  }

  //handle shadow and margin
  switch (widgetMaskType) {
    case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.PHOTO: {
      width = width + 25;
      height = height + 40;
      break;
    }
    case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LETS_TALK: {
      width = width + (widgetMaskText ? 30 : 35);
      height = height + 35;
      break;
    }
    case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.SOLO_CHAT: {
      width = width + 45;
      height = height + 50;
      break;
    }
    case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.ANIMATED_PHOTO: {
      if (!widgetMaskText) {
        const paddingForRipple = 10;
        width = width + 2 * paddingForRipple;
        height = height + 2 * paddingForRipple;
      }
      break;
    }
    default:
      break;
  }

  // widget text
  if (widgetMaskText) {
    switch (widgetMaskType) {
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LOGO: {
        const widgetMaskTextWidth =
          utilsService.getTextWidthAccToStyles(widgetMaskText);
        const textWidthInsideMask = 30;
        const paddingAroundText = 11 * 2;
        const heightOfTextOutsideMask = 25;
        width =
          width +
          (widgetMaskTextWidth + paddingAroundText - textWidthInsideMask);
        height = height + heightOfTextOutsideMask;
        break;
      }
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.PHOTO: {
        const widgetMaskTextWidth =
          utilsService.getTextWidthAccToStyles(widgetMaskText);
        const paddingAroundText = 20 * 2;
        width = width + (widgetMaskTextWidth + paddingAroundText);
        break;
      }
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.LETS_TALK: {
        const widgetMaskTextWidth =
          utilsService.getTextWidthAccToStyles(widgetMaskText);
        const paddingAroundText = 10 * 2;
        const marginRight = 10;
        width = width + (widgetMaskTextWidth + paddingAroundText + marginRight);
        break;
      }
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.SOLO_CHAT: {
        const widgetMaskTextWidth =
          utilsService.getTextWidthAccToStyles(widgetMaskText);
        const textWidthInsideMask = 25 + 23;
        const paddingAroundText = 11 * 2;
        width =
          width +
          (widgetMaskTextWidth + paddingAroundText - textWidthInsideMask);
        break;
      }
      case VIDABOO_CONSTANTS.WIDGET_MASK_TYPE.ANIMATED_PHOTO: {
        const widgetMaskTextWidth =
          utilsService.getTextWidthAccToStyles(widgetMaskText);
        const textWidthInsideMask = 20;
        const paddingAroundText = 10 * 2;
        const heightOfTextOutsideMask = 10;
        const paddingForRipple = 10;
        const spaceForShadowAroundText = 10;
        width =
          width +
          (widgetMaskTextWidth +
            paddingAroundText -
            textWidthInsideMask -
            2 * paddingForRipple) +
          spaceForShadowAroundText;
        height = height + heightOfTextOutsideMask + spaceForShadowAroundText;
        break;
      }
      default:
        console.debug('Unsupported mask type: ', widgetMaskType);
    }
  }
  return {height, width};
};

service.getTextWidthAccToStyles = (text, font = '16px Outfit') => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  context.font = font;
  return context.measureText(text)?.width;
};

service.getBgPatternComponentOrName = (
  patternName,
  themePreset,
  shouldReturnName,
) => {
  const patternMap = {
    [VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.NONE]: <></>,
    [VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.ORBITAL]: <BgOrbitalPattern />,
    [VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.WIREFRAME]: (
      <BgWireframePattern />
    ),
    [VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.WAVEFORM]: <BgWaveformPattern />,
    [VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.BUBBLES]: <BgBubblesPattern />,
    [VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.BLURBOX]: <BgBlurboxPattern />,
    [VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.HEXAFLOW]: <BgHexaflowPattern />,
  };

  if (!patternName) {
    const themeDefaultMap = {
      [VIDABOO_CONSTANTS.MAIN_THEME.CONSOLTO_LITE.type]:
        VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.WAVEFORM,
      [VIDABOO_CONSTANTS.MAIN_THEME.RED.type]:
        VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.ORBITAL,
      [VIDABOO_CONSTANTS.MAIN_THEME.CONSOLTO_PURPLE.type]:
        VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.HEXAFLOW,
    };
    patternName = themeDefaultMap[themePreset];
  }

  if (!patternName)
    return shouldReturnName ? (
      VIDABOO_CONSTANTS.WIDGET_BG_PATTERN_NAME.NONE
    ) : (
      <></>
    );

  return shouldReturnName ? patternName : patternMap[patternName] || <></>;
};

service.getVersionInfoFromVersionCheckResponse = (response, osKeyString) => {
  const major = response.data.version[osKeyString]?.major;
  const minor = response.data.version[osKeyString]?.minor;
  return {major, minor};
};

service.setPageTitle = (title) => {
  if (title && typeof title === 'string') {
    document.title = title;
  }
};

service.setFavicon = (imageUrl) => {
  if (imageUrl && typeof imageUrl === 'string') {
    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll("link[rel*='icon']");
    existingFavicons.forEach((favicon) => favicon.remove());

    // Create and add new favicon link
    const link = document.createElement('link');
    link.rel = 'icon';
    link.type = 'image/x-icon';
    link.href = imageUrl;
    document.head.appendChild(link);
  }
};

service.init();
const utilsService = {...service};
export default utilsService;
